import React, { ReactNode, useCallback } from 'react';
import { ValidOption, ValidOptionalInput } from './types';
import ValidInputError from './ValidInputError';
import ValidErrorMarker from './ValidErrorMarker';
import ValidInputLabel from './ValidInputLabel';

// TYPE
export type ValidPropDroplistText = {
    key: string;
    lbl: string;
    val: string;
    def: string;
    options: ValidOption[];
    err: string;
};

// VIEW MODEL
export function validPropDroplistTextVM(
    key: string,
    lbl?: string,
    val?: any,
    def?: string,
    options?: ValidOption[],
): ValidPropDroplistText {
    return {
        key: key,
        lbl: !!lbl ? lbl : '',
        val: !!val ? val : !!def ? def : '',
        def: def || '',
        options: Array.isArray(options) ? options : [],
        err: '',
    };
}

// VALIDATOR
export function validatePropDroplistText(prop: ValidPropDroplistText, req?: boolean): boolean {
    prop.err = '';

    const val = typeof prop.val === 'string' ? prop.val.trim() : '';
    if (!!req && val.length < 1) {
        prop.err = 'required';
    }

    return !!!prop.err;
}

// COMPONENT
export type ValidInputDroplistTextProps = ValidOptionalInput & {
    prop: ValidPropDroplistText;
    disabled?: boolean;
    onChange: (prop: string, val: string | number) => void;
    fullWidth?: boolean;
    interpretAs?: 'string' | 'number';
    noMinWidth?: boolean;
    customMaxWidth?: number;
    children?: ReactNode;
};

const ValidInputDroplistText = ({
    prop,
    disabled,
    onChange,
    checked,
    onCheckboxChange,
    fullWidth,
    interpretAs = 'string',
    noMinWidth = false,
    customMaxWidth,
    children,
}: ValidInputDroplistTextProps): JSX.Element => {
    const showCheckbox = !!onCheckboxChange && checked !== undefined;

    const handleCheckboxChange = useCallback((): void => {
        if (showCheckbox) {
            onCheckboxChange(!checked);
        }
    }, [checked, onCheckboxChange, showCheckbox]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>): void => {
            const newValue = interpretAs === 'number' ? parseInt(e.target.value) : e.target.value;
            onChange(prop.key, newValue);
        },
        [interpretAs, onChange, prop.key],
    );

    return (
        <div className="component-valid-input input-drop">
            <div className={`input-wrapper ${fullWidth ? 'full-width' : ''}`}>
                {prop.lbl ? (
                    <ValidInputLabel
                        label={prop.lbl}
                        onClick={showCheckbox ? handleCheckboxChange : undefined}
                        noMinWidth={noMinWidth}
                    />
                ) : null}
                <select
                    id={`${prop.key}-${prop.lbl}`}
                    style={customMaxWidth ? { maxWidth: customMaxWidth } : {}} // needs to be inline for custom override
                    className="valid-input input-drop-list-text"
                    value={prop.val}
                    onChange={handleChange}
                    disabled={disabled}
                >
                    {prop.options.map((opt, idx) => (
                        <option
                            key={idx}
                            value={opt.val}
                        >
                            {opt.lbl}
                        </option>
                    ))}
                </select>
                <ValidErrorMarker showMarker={!!prop.err} />
                {children}
            </div>
            <ValidInputError
                err={prop.err}
                showMsg
            />
        </div>
    );
};

export default ValidInputDroplistText;
